import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { useModals } from "../components/common/ModalsProvider.js";
import { FiscalYearSelector } from "../components/fiscal/FiscalYearSelector.js";
import { KpiCard } from "../components/fiscal/KpiCard.js";
import {
  FiscalCard,
  FiscalShell,
  LoadingSkeleton,
  fiscalInputClass,
} from "../components/fiscal/Shell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useFiscalSelection } from "../hooks/useFiscalSelection.js";
import { useT } from "../i18n/index.js";
import { deltaColorClass, formatMoney, formatPctSigned, toNumber } from "../lib/format-fr.js";
import { trpc, type AppRouter } from "../lib/trpc.js";
import type { inferRouterOutputs } from "@trpc/server";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type BudgetRow = NonNullable<RouterOutputs["fiscalBudgets"]["getByFiscalYear"]>;
type CalcRow = NonNullable<RouterOutputs["fiscalCalculations"]["getByFiscalYear"]>;

const INPUT_LINES: ReadonlyArray<{ key: BudgetField; label: string; group: string }> = [
  { key: "caHt", label: "Chiffre d'affaires HT", group: "Aggregate" },
  { key: "achatsConsommes", label: "Achats consommés", group: "Aggregate" },
  { key: "autresChargesVariables", label: "Autres charges variables", group: "Aggregate" },
  {
    key: "fournituresConsommables",
    label: "Fournitures & consommables",
    group: "Charges externes",
  },
  {
    key: "locationsChargesLocatives",
    label: "Locations & charges locatives",
    group: "Charges externes",
  },
  { key: "primesAssurance", label: "Primes d'assurance", group: "Charges externes" },
  {
    key: "intermediairesHonoraires",
    label: "Intermédiaires & honoraires",
    group: "Charges externes",
  },
  { key: "deplacementsReception", label: "Déplacements & réception", group: "Charges externes" },
  { key: "fraisBancaires", label: "Frais bancaires", group: "Charges externes" },
  { key: "impotsTaxes", label: "Impôts & taxes", group: "Outras cargas" },
  { key: "chargesPersonnel", label: "Charges de personnel", group: "Outras cargas" },
  { key: "dotationsAmortissements", label: "Dotations amortissements", group: "Outras cargas" },
  { key: "dotationsProvisions", label: "Dotations provisions", group: "Outras cargas" },
  {
    key: "autresChargesExploitation",
    label: "Autres charges d'exploitation",
    group: "Outras cargas",
  },
  { key: "impotSocietes", label: "Impôt sur les sociétés", group: "Outras cargas" },
  { key: "subventionsExploitation", label: "Subventions d'exploitation", group: "Outros produtos" },
  {
    key: "autresProduitsExploitation",
    label: "Autres produits d'exploitation",
    group: "Outros produtos",
  },
];

type BudgetField =
  | "caHt"
  | "achatsConsommes"
  | "autresChargesVariables"
  | "fournituresConsommables"
  | "locationsChargesLocatives"
  | "primesAssurance"
  | "intermediairesHonoraires"
  | "deplacementsReception"
  | "fraisBancaires"
  | "impotsTaxes"
  | "chargesPersonnel"
  | "dotationsAmortissements"
  | "dotationsProvisions"
  | "autresChargesExploitation"
  | "impotSocietes"
  | "subventionsExploitation"
  | "autresProduitsExploitation";

type VariationsForm = Partial<Record<`${BudgetField}VariationPct`, string>>;
type OverridesForm = Partial<Record<BudgetField, string>>;

export default function FiscalBudget() {
  const { companyId, fiscalYearId, setCompanyId, setFiscalYearId } = useFiscalSelection();
  const { user } = useAuth();
  const modals = useModals();
  const t = useT();
  // F-roles — viewer (somente leitura) nunca vê controles de escrita.
  const canEdit = useCanEdit();
  const isAdmin =
    canEdit &&
    (user?.isSuperAdmin ||
      (user?.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin") ??
        false));

  // F5.9 — lista TODAS as revisões do FY (não arquivadas) e elege qual editar.
  const listQuery = trpc.fiscalBudgets.listByFiscalYear.useQuery(
    {
      companyId: companyId ?? 0,
      fiscalYearId: fiscalYearId ?? 0,
      includeArchived: false,
    },
    { enabled: companyId !== null && fiscalYearId !== null, retry: false },
  );
  const revisions = listQuery.data ?? [];
  const [selectedBudgetId, setSelectedBudgetId] = useState<number | null>(null);

  // Auto-seleciona a ativa quando a lista resolve
  useEffect(() => {
    if (selectedBudgetId !== null) return;
    const active = revisions.find((r) => r.isActive);
    if (active) setSelectedBudgetId(active.id);
    else if (revisions[0]) setSelectedBudgetId(revisions[0].id);
  }, [revisions, selectedBudgetId]);

  // Reseta seleção ao trocar de FY
  useEffect(() => {
    setSelectedBudgetId(null);
  }, [fiscalYearId]);

  const budget = useMemo(
    () => revisions.find((r) => r.id === selectedBudgetId) ?? null,
    [revisions, selectedBudgetId],
  );

  // Realizado N: busca calculation do baseline FY referenciado pelo budget.
  const baselineCalcQuery = trpc.fiscalCalculations.getByFiscalYear.useQuery(
    {
      companyId: companyId ?? 0,
      fiscalYearId: budget?.baselineFiscalYearId ?? 0,
    },
    {
      enabled: budget !== null && budget !== undefined,
    },
  );
  const baselineCalc = baselineCalcQuery.data;

  const utils = trpc.useUtils();
  const createMutation = trpc.fiscalBudgets.create.useMutation({
    onSuccess: (created) => {
      utils.fiscalBudgets.invalidate();
      if (created) setSelectedBudgetId(created.id);
    },
  });
  const updateMutation = trpc.fiscalBudgets.update.useMutation({
    onSuccess: () => utils.fiscalBudgets.invalidate(),
  });
  const cloneMutation = trpc.fiscalBudgets.clone.useMutation({
    onSuccess: (cloned) => {
      utils.fiscalBudgets.invalidate();
      if (cloned) setSelectedBudgetId(cloned.id);
    },
    onError: (e) => setError(e.message),
  });
  const setActiveMutation = trpc.fiscalBudgets.setActive.useMutation({
    onSuccess: () => {
      utils.fiscalBudgets.invalidate();
      setFeedback(t("fiscalBudget.toastSetActive"));
    },
    onError: (e) => setError(e.message),
  });
  const archiveMutation = trpc.fiscalBudgets.archive.useMutation({
    onSuccess: () => {
      utils.fiscalBudgets.invalidate();
      setSelectedBudgetId(null);
      setFeedback(t("fiscalBudget.toastArchived"));
    },
    onError: (e) => setError(e.message),
  });

  // Form state — populado a partir do budget existente
  const [variations, setVariations] = useState<VariationsForm>({});
  const [overrides, setOverrides] = useState<OverridesForm>({});
  const [overrideToggle, setOverrideToggle] = useState<Partial<Record<BudgetField, boolean>>>({});
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (!budget) return;
    const v: VariationsForm = {};
    for (const { key } of INPUT_LINES) {
      v[`${key}VariationPct`] = String(budget[`${key}VariationPct`] ?? "");
    }
    setVariations(v);
    setOverrides({});
    setOverrideToggle({});
    setFeedback(null);
  }, [budget?.id, budget?.lastComputedAt]);

  const toolbar = (
    <FiscalYearSelector
      companyId={companyId}
      fiscalYearId={fiscalYearId}
      onChangeCompany={setCompanyId}
      onChangeFiscalYear={(fy) => setFiscalYearId(fy?.id ?? null)}
    />
  );

  function parseNum(s: string | undefined): number | undefined {
    if (s === undefined || s.trim() === "") return undefined;
    const n = Number(s.replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  }

  async function handleSave() {
    if (!companyId || !budget) return;
    setError(null);
    setFeedback(null);

    const variationsPayload: Record<string, number> = {};
    for (const { key } of INPUT_LINES) {
      const n = parseNum(variations[`${key}VariationPct`]);
      if (n !== undefined) variationsPayload[`${key}VariationPct`] = n;
    }

    const overridesPayload: Record<string, number> = {};
    for (const { key } of INPUT_LINES) {
      if (overrideToggle[key]) {
        const n = parseNum(overrides[key]);
        if (n !== undefined) overridesPayload[key] = n;
      }
    }

    try {
      await updateMutation.mutateAsync({
        companyId,
        budgetId: budget.id,
        variations: Object.keys(variationsPayload).length ? variationsPayload : undefined,
        overrides: Object.keys(overridesPayload).length ? overridesPayload : undefined,
      });
      setFeedback(t("fiscalBudget.toastSaved"));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleCreate() {
    if (!companyId || !fiscalYearId) return;
    setError(null);
    // Se já existe alguma revisão, pede nome (UNIQUE). Senão default.
    const name =
      revisions.length === 0
        ? "default"
        : await modals.promptReason({
            title: t("fiscalBudget.newRevisionTitle"),
            message: t("fiscalBudget.newRevisionMessage"),
            placeholder: t("fiscalBudget.newRevisionPlaceholder"),
            required: true,
            confirmLabel: t("fiscalBudget.createLabel"),
          });
    if (!name) return;
    try {
      await createMutation.mutateAsync({
        companyId,
        fiscalYearId,
        name,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleClone() {
    if (!companyId || !budget) return;
    setError(null);
    const newName = await modals.promptReason({
      title: `${t("fiscalBudget.cloneLabel")} "${budget.name}"`,
      message: t("fiscalBudget.cloneMessage"),
      placeholder: `${t("fiscalBudget.placeholderExPre")} ${budget.name}-v2`,
      required: true,
      confirmLabel: t("fiscalBudget.cloneLabel"),
    });
    if (!newName) return;
    cloneMutation.mutate({
      companyId,
      sourceBudgetId: budget.id,
      newName,
    });
  }

  async function handleSetActive() {
    if (!companyId || !budget) return;
    setError(null);
    setActiveMutation.mutate({ companyId, budgetId: budget.id });
  }

  async function handleArchive() {
    if (!companyId || !budget) return;
    setError(null);
    const reason = await modals.promptReason({
      title: `${t("fiscalBudget.archiveTitlePre")} "${budget.name}"`,
      message: t("fiscalBudget.archiveMessage"),
      placeholder: t("fiscalBudget.archivePlaceholder"),
      required: true,
      confirmLabel: t("fiscalBudget.archiveLabel"),
      tone: "danger",
    });
    if (!reason) return;
    archiveMutation.mutate({ companyId, budgetId: budget.id, reason });
  }

  if (companyId === null || fiscalYearId === null) {
    return (
      <FiscalShell subtitle={t("fiscalBudget.subtitle")} toolbar={toolbar}>
        <FiscalCard>
          <p className="text-sm text-neutral-400">{t("fiscalBudget.selectCompanyFy")}</p>
        </FiscalCard>
      </FiscalShell>
    );
  }

  if (listQuery.isLoading) {
    return (
      <FiscalShell subtitle={t("fiscalBudget.subtitle")} toolbar={toolbar}>
        <FiscalCard>
          <LoadingSkeleton rows={8} />
        </FiscalCard>
      </FiscalShell>
    );
  }

  if (!budget) {
    return (
      <FiscalShell subtitle={t("fiscalBudget.subtitle")} toolbar={toolbar}>
        <FiscalCard
          title={t("fiscalBudget.noBudgetTitle")}
          subtitle={t("fiscalBudget.noBudgetSubtitle")}
          action={
            canEdit ? (
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium disabled:opacity-50"
              >
                {createMutation.isPending
                  ? t("fiscalBudget.creating")
                  : t("fiscalBudget.createBudgetBtn")}
              </button>
            ) : null
          }
        >
          <p className="text-sm text-neutral-400">{t("fiscalBudget.noBudgetHelp")}</p>
          {error && <p className="text-sm text-red-400 mt-3">{error}</p>}
        </FiscalCard>
      </FiscalShell>
    );
  }

  const totalActions =
    createMutation.isPending ||
    updateMutation.isPending ||
    cloneMutation.isPending ||
    setActiveMutation.isPending ||
    archiveMutation.isPending;

  return (
    <FiscalShell subtitle={t("fiscalBudget.subtitle")} toolbar={toolbar}>
      {/* F5.9 — Barra de revisões */}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-3 flex items-center gap-2 flex-wrap">
        <label className="text-xs uppercase tracking-wider text-neutral-500">
          {t("fiscalBudget.revisionLabel")}:
        </label>
        <select
          value={selectedBudgetId ?? ""}
          onChange={(e) => setSelectedBudgetId(Number(e.target.value))}
          disabled={totalActions}
          className={fiscalInputClass + " w-64"}
        >
          {revisions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
              {r.isActive ? ` · ${t("fiscalBudget.activeSuffix")}` : ""}
            </option>
          ))}
        </select>
        {budget.isActive && (
          <span className="rounded px-2 py-0.5 text-xs bg-lime/15 text-lime">
            {t("fiscalBudget.activeBadge")}
          </span>
        )}

        <div className="flex-1" />

        {canEdit && (
          <button
            onClick={handleCreate}
            disabled={totalActions}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-3 py-1 text-xs font-medium"
          >
            {t("fiscalBudget.newRevisionBtn")}
          </button>
        )}
        {canEdit && (
          <button
            onClick={handleClone}
            disabled={totalActions}
            className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime px-3 py-1 text-xs"
          >
            {t("fiscalBudget.cloneLabel")}
          </button>
        )}
        {revisions.length >= 2 && (
          <Link
            href="/fiscal/budget/compare"
            className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime px-3 py-1 text-xs"
          >
            {t("fiscalBudget.compareBtn")}
          </Link>
        )}
        {isAdmin && !budget.isActive && (
          <button
            onClick={handleSetActive}
            disabled={totalActions}
            className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1 text-xs"
          >
            {t("fiscalBudget.setActiveBtn")}
          </button>
        )}
        {isAdmin && (
          <button
            onClick={handleArchive}
            disabled={totalActions}
            className="rounded-md border border-red-500/40 hover:border-red-500 text-red-300 px-3 py-1 text-xs"
          >
            {t("fiscalBudget.archiveLabel")}
          </button>
        )}
      </div>

      <FiscalCard
        title={`${t("fiscalBudget.identificationTitle")} · "${budget.name}"`}
        subtitle={`${t("fiscalBudget.baselinePre")} FY #${budget.baselineFiscalYearId} (${budget.baselineSource}) · ${t("fiscalBudget.updatedAtPre")} ${budget.lastComputedAt ? new Date(budget.lastComputedAt).toLocaleString("fr-FR") : "—"}`}
      />

      {/* KPIs projetados */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label={t("fiscalBudget.kpiMargeGlobale")}
          value={formatMoney(budget.margeGlobaleProjected)}
          secondaryLabel={t("fiscalBudget.realN")}
          secondaryValue={formatMoney(baselineCalc?.margeGlobaleCumulative ?? null)}
        />
        <KpiCard
          label={t("fiscalBudget.kpiVa")}
          value={formatMoney(budget.vaProjected)}
          secondaryLabel={t("fiscalBudget.realN")}
          secondaryValue={formatMoney(baselineCalc?.vaCumulative ?? null)}
        />
        <KpiCard
          label={t("fiscalBudget.kpiEbe")}
          value={formatMoney(budget.ebeProjected)}
          secondaryLabel={t("fiscalBudget.realN")}
          secondaryValue={formatMoney(baselineCalc?.ebeCumulative ?? null)}
        />
        <KpiCard
          label={t("fiscalBudget.kpiRn")}
          value={formatMoney(budget.resultatNetProjected)}
          secondaryLabel={t("fiscalBudget.realN")}
          secondaryValue={formatMoney(baselineCalc?.resultatNetCumulative ?? null)}
          tone={(toNumber(budget.resultatNetProjected) ?? 0) < 0 ? "red" : "neutral"}
        />
        <KpiCard
          label={t("fiscalBudget.kpiCaf")}
          value={formatMoney(budget.cafProjected)}
          secondaryLabel={t("fiscalBudget.realN")}
          secondaryValue={formatMoney(baselineCalc?.cafCumulative ?? null)}
        />
        <KpiCard
          label={t("fiscalBudget.kpiSeuilEco")}
          value={formatMoney(budget.seuilEconomiqueProjected)}
          secondaryLabel={t("fiscalBudget.realN")}
          secondaryValue={formatMoney(baselineCalc?.seuilEconomique ?? null)}
        />
        <KpiCard
          label={t("fiscalBudget.kpiSeuilFin")}
          value={formatMoney(budget.seuilFinancierProjected)}
          secondaryLabel={t("fiscalBudget.realN")}
          secondaryValue={formatMoney(baselineCalc?.seuilFinancier ?? null)}
        />
        <KpiCard
          label={t("fiscalBudget.kpiPointMort")}
          value={
            budget.pointMortJoursProjected
              ? `${Number(budget.pointMortJoursProjected).toFixed(0)} ${t("fiscalBudget.daysUnit")}`
              : "—"
          }
          secondaryLabel={t("fiscalBudget.realN")}
          secondaryValue={
            baselineCalc?.pointMortJours
              ? `${Number(baselineCalc.pointMortJours).toFixed(0)} ${t("fiscalBudget.daysUnit")}`
              : "—"
          }
        />
      </div>

      {/* Form de variations + overrides */}
      <FiscalCard
        title={t("fiscalBudget.variationsTitle")}
        subtitle={t("fiscalBudget.variationsSubtitle")}
        action={
          <div className="flex items-center gap-2">
            {feedback && <span className="text-xs text-lime">{feedback}</span>}
            {canEdit && (
              <button
                onClick={handleSave}
                disabled={totalActions}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
              >
                {totalActions ? t("fiscalBudget.saving") : t("fiscalBudget.save")}
              </button>
            )}
          </div>
        }
        padded={false}
      >
        {error && (
          <div className="px-5 pt-4">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-neutral-500">
            <tr className="border-b border-neutral-800">
              <th className="text-left px-3 py-2">{t("fiscalBudget.colRubrique")}</th>
              <th className="text-right px-3 py-2 font-mono w-32">
                {t("fiscalBudget.colRealiseN")}
              </th>
              <th className="text-right px-3 py-2 font-mono w-32">
                {t("fiscalBudget.colVariationPct")}
              </th>
              <th className="text-right px-3 py-2 font-mono w-32">
                {t("fiscalBudget.colSuggested")}
              </th>
              <th className="text-right px-3 py-2 font-mono w-40">
                {t("fiscalBudget.colEffective")}
              </th>
              <th className="text-right px-3 py-2 font-mono w-28">
                {t("fiscalBudget.colDeltaVsReal")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-900">
            {groupLines(INPUT_LINES).map(({ group, lines }, gi) => (
              <RenderGroup
                key={gi}
                group={group}
                lines={lines}
                canEdit={canEdit}
                budget={budget}
                baselineCalc={baselineCalc ?? null}
                variations={variations}
                onVariation={(key, v) =>
                  setVariations((prev) => ({
                    ...prev,
                    [`${key}VariationPct`]: v,
                  }))
                }
                overrideToggle={overrideToggle}
                onToggleOverride={(key) =>
                  setOverrideToggle((prev) => ({
                    ...prev,
                    [key]: !prev[key],
                  }))
                }
                overrides={overrides}
                onOverride={(key, v) => setOverrides((prev) => ({ ...prev, [key]: v }))}
              />
            ))}
          </tbody>
        </table>
      </FiscalCard>
    </FiscalShell>
  );
}

type LineGroup = { group: string; lines: typeof INPUT_LINES };

function groupLines(lines: typeof INPUT_LINES): LineGroup[] {
  const out: LineGroup[] = [];
  for (const l of lines) {
    let g = out.find((x) => x.group === l.group);
    if (!g) {
      g = { group: l.group, lines: [] as unknown as typeof INPUT_LINES };
      out.push(g);
    }
    (g.lines as Array<(typeof INPUT_LINES)[number]>).push(l);
  }
  return out;
}

function RenderGroup({
  group,
  lines,
  canEdit,
  budget,
  baselineCalc,
  variations,
  onVariation,
  overrideToggle,
  onToggleOverride,
  overrides,
  onOverride,
}: {
  group: string;
  lines: typeof INPUT_LINES;
  canEdit: boolean;
  budget: BudgetRow;
  baselineCalc: CalcRow | null;
  variations: VariationsForm;
  onVariation: (key: BudgetField, v: string) => void;
  overrideToggle: Partial<Record<BudgetField, boolean>>;
  onToggleOverride: (key: BudgetField) => void;
  overrides: OverridesForm;
  onOverride: (key: BudgetField, v: string) => void;
}) {
  const t = useT();
  const lineLabels: Record<BudgetField, string> = {
    caHt: t("fiscalBudget.lineCaHt"),
    achatsConsommes: t("fiscalBudget.lineAchatsConsommes"),
    autresChargesVariables: t("fiscalBudget.lineAutresChargesVariables"),
    fournituresConsommables: t("fiscalBudget.lineFournituresConsommables"),
    locationsChargesLocatives: t("fiscalBudget.lineLocationsChargesLocatives"),
    primesAssurance: t("fiscalBudget.linePrimesAssurance"),
    intermediairesHonoraires: t("fiscalBudget.lineIntermediairesHonoraires"),
    deplacementsReception: t("fiscalBudget.lineDeplacementsReception"),
    fraisBancaires: t("fiscalBudget.lineFraisBancaires"),
    impotsTaxes: t("fiscalBudget.lineImpotsTaxes"),
    chargesPersonnel: t("fiscalBudget.lineChargesPersonnel"),
    dotationsAmortissements: t("fiscalBudget.lineDotationsAmortissements"),
    dotationsProvisions: t("fiscalBudget.lineDotationsProvisions"),
    autresChargesExploitation: t("fiscalBudget.lineAutresChargesExploitation"),
    impotSocietes: t("fiscalBudget.lineImpotSocietes"),
    subventionsExploitation: t("fiscalBudget.lineSubventionsExploitation"),
    autresProduitsExploitation: t("fiscalBudget.lineAutresProduitsExploitation"),
  };
  const groupLabels: Record<string, string> = {
    Aggregate: t("fiscalBudget.groupAggregate"),
    "Charges externes": t("fiscalBudget.groupChargesExternes"),
    "Outras cargas": t("fiscalBudget.groupOtherCharges"),
    "Outros produtos": t("fiscalBudget.groupOtherIncome"),
  };
  return (
    <>
      <tr className="bg-neutral-800/30">
        <td colSpan={6} className="px-3 py-1.5 text-xs uppercase tracking-wider text-neutral-500">
          {groupLabels[group] ?? group}
        </td>
      </tr>
      {lines.map((line) => {
        const realisé = baselineCalc ? readBaselineField(baselineCalc, line.key) : null;
        const suggested = budget[`${line.key}Suggested` as keyof BudgetRow] as string | null;
        const effective = budget[line.key as keyof BudgetRow] as string | null;
        const variationVal = variations[`${line.key}VariationPct`] ?? "";
        const isOverride = overrideToggle[line.key] ?? false;
        const overrideVal = overrides[line.key] ?? "";

        const delta =
          toNumber(effective) !== null && toNumber(realisé) !== null
            ? (toNumber(effective) ?? 0) - (toNumber(realisé) ?? 0)
            : null;

        return (
          <tr key={line.key}>
            <td className="px-3 py-1.5 pl-6 text-neutral-200">{lineLabels[line.key]}</td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums text-neutral-400">
              {formatMoney(realisé)}
            </td>
            <td className="px-3 py-1.5 text-right">
              <div className="flex items-center justify-end gap-1">
                {canEdit ? (
                  <input
                    type="text"
                    inputMode="decimal"
                    value={variationVal}
                    onChange={(e) => onVariation(line.key, e.target.value)}
                    className={`${fiscalInputClass} w-20 text-right font-mono`}
                    placeholder="0"
                  />
                ) : (
                  <span className="font-mono tabular-nums text-neutral-400">
                    {variationVal || "0"}
                  </span>
                )}
                <span className="text-neutral-500 text-xs">%</span>
              </div>
            </td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums text-neutral-500">
              {formatMoney(suggested)}
            </td>
            <td className="px-3 py-1.5 text-right">
              {!canEdit ? (
                <span className="font-mono tabular-nums text-neutral-200">
                  {formatMoney(effective)}
                </span>
              ) : isOverride ? (
                <input
                  type="text"
                  inputMode="decimal"
                  value={overrideVal}
                  onChange={(e) => onOverride(line.key, e.target.value)}
                  className={`${fiscalInputClass} w-32 text-right font-mono`}
                  placeholder={String(effective)}
                />
              ) : (
                <button
                  onClick={() => onToggleOverride(line.key)}
                  className="font-mono tabular-nums text-neutral-200 hover:text-lime hover:underline"
                  title={t("fiscalBudget.overrideTooltip")}
                >
                  {formatMoney(effective)}
                </button>
              )}
              {canEdit && isOverride && (
                <button
                  onClick={() => onToggleOverride(line.key)}
                  className="ml-2 text-xs text-neutral-500 hover:text-neutral-300"
                >
                  {t("fiscalBudget.cancel")}
                </button>
              )}
            </td>
            <td
              className={`px-3 py-1.5 text-right font-mono tabular-nums ${deltaColorClass(delta)}`}
            >
              {delta === null
                ? "—"
                : formatPctSigned(
                    (toNumber(realisé) ?? 0) !== 0
                      ? (delta / Math.abs(toNumber(realisé)!)) * 100
                      : null,
                    { alreadyPct: true },
                  )}
            </td>
          </tr>
        );
      })}
    </>
  );
}

// Mapeia o nome do campo budget para o equivalente na fiscal_year_calculations.
// As 17 chaves de budget coincidem 1:1 com colunas da row de calculations.
function readBaselineField(calc: CalcRow, key: BudgetField): string | number | null {
  const v = calc[key as keyof typeof calc];
  if (typeof v === "string" || typeof v === "number") return v;
  return null;
}
